import React, {useState, useCallback, useEffect} from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import { Menu, MenuItem, IconButton, Tooltip } from '@mui/material';
import { MoreVert as MoreVertIcon, Info as InfoIcon } from '@mui/icons-material';

import AddSamplesFileListItem from './AddSamplesFileListItem';
import SamplesList from './SamplesList';
import ExportIdentifiedSpeciesMenuItem from '../IdentifiedSpecies/ExportIdentifiedSpeciesMenuItem';
import ExportKgASTPredictionsMenuItem from '../IdentifiedSpecies/ExportKgASTPredictionsMenuItem';
import FileDropZone from './AddSamplesDropZone';
import TriggerClonalityAnalysisButtonOrStatusIcon from './TriggerClonalityAnalysisButton.jsx';
import UpdateTextField from '../TextFields/UpdateTextField';

import ontLogo from '../ont_logo.svg';
import illuminaLogo from '../illumina_logo.svg';
import { useActiveAnalysis, useSamples } from '../services/api/KeynomeAPIHooks';
import { useUpdateDatasetName } from '../services/api/KeynomeAPIMutations';
import ArchiveDatasetDialog from './ArchiveSampleDialog';
import MoveDatasetDialog from '../Runs/MoveDatasetDialog';
import { slugify } from '../utils';
import { useQueryClient } from '@tanstack/react-query';


function isIlluminaDataset(activeRun) {
    if (!activeRun) {
        return false;
    }

    return activeRun.attributes.sequencing_platform === 'illumina'
        || activeRun.attributes.platform === 'illumina';
}

function extractFileBasename(filename) {
    return filename
    .replace(/_001\.fastq\.gz$/, '.fastq.gz')
    .replace('.fastq.gz', '')
    .replace('./', '');
}

function basenameIsR1File(basename) {  
    return basename.match(/_(R?1)(_001)?$/) !== null;
}

function extractSampleNameFromFilename(filename, isIllumina) {
    const basename = extractFileBasename(filename)
    if (isIllumina) {
        return basename.replace(/_(R?[12])(_001)?$/, '');
    }

    return basename
}

function AddSamplesDialog(props) {
    const { 
        open, 
        setOpen, 
        activeRun, 
        uploadFile, 
        backgroundUploadingFilesCt, 
        activeProject, 
        setReloadRuns,
        setUpdateRunDialogOpen,
    } = props;
    const { setUserErrorMessage } = useOutletContext();
    const [ archiveDialogOpen, setArchiveDialogOpen ] = useState(false);
    const [ moveDatasetDialogOpen, setMoveDatasetDialogOpen ] = useState(false);
    const [ stagedFilesByDataset, setStagedFilesbyDataset] = useState({});
    const [ confirmClose, setConfirmClose ] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const { mutateAsync: updateDataset } = useUpdateDatasetName();
  
    const { data: samples } = useSamples(activeRun?.id);

    const isIllumina = isIlluminaDataset(activeRun);

    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (activeRun) {
            setStagedFilesbyDataset(prev => {
                const newStagedFiles = {...prev};
                if (!newStagedFiles[activeRun.id]) newStagedFiles[activeRun.id] = {};
                return newStagedFiles;
            })
        }
    }, [activeRun, setStagedFilesbyDataset])

    // Adds staged file object to stagedFilesByDataset state object by datasetId and sampleName
    // If sampleName does not exist, create a new object with the file
    // If sampleName exists, update the file object with the new file
    // To be used on file drop event
    const addStagedFile = useCallback(file => {
        setStagedFilesbyDataset(prev => {
            const newStagedFiles = {...prev};
            const sampleName = extractSampleNameFromFilename(file.name, isIllumina)

            if (!newStagedFiles[activeRun.id][sampleName]) {
                newStagedFiles[activeRun.id][sampleName] = {
                    datasetId: activeRun.id,
                    sampleName: sampleName,
                    R1: null,
                    R2: null,
                    sampleType: 'isolate',
                    status: null,
                    canUpload: null,
                }
            }
            const fileKey = isIllumina && !basenameIsR1File(extractFileBasename(file.name)) ? 'R2' : 'R1';
            newStagedFiles[activeRun.id][sampleName][fileKey] = file;
            return newStagedFiles;
        })
    }, [setStagedFilesbyDataset, activeRun, isIllumina])

    // Deletes staged file object from stagedFilesByDataset state object by datasetId and sampleName
    // To be used when upload cancelled
    const removeStagedFile = useCallback((datasetId, sampleName) => {
        setStagedFilesbyDataset(prev => {
            const newStagedFiles = {...prev};
            delete newStagedFiles[datasetId][sampleName];
            return newStagedFiles;
        })
    }, [setStagedFilesbyDataset])

    // Generic method to set an attribute on a staged file object by datasetId and sampleName
    // To be used to set the sampleType, status, and canUpload attributes
    const setStagedFileAttribute = useCallback((datasetId, sampleName, attribute, value) => {
        setStagedFilesbyDataset(prev => {
            const newStagedFiles = {...prev};
            if (newStagedFiles[datasetId][sampleName]) newStagedFiles[datasetId][sampleName][attribute] = value;
            return newStagedFiles;
        })
    }, [setStagedFilesbyDataset])

    const visibleSamples = (samples || []).filter(sample => 
        !stagedFilesByDataset[activeRun?.id] ||
        !stagedFilesByDataset[activeRun.id][sample.attributes.sample_name]
        || stagedFilesByDataset[activeRun.id][sample.attributes.sample_name].status === 'success' // show samples were staged and upload completed
        || stagedFilesByDataset[activeRun.id][sample.attributes.sample_name].canUpload === false // show samples that were analyzed successfully and incorrectly re-staged
    );

    const getStagedFilesNotUploadedCt = () => {
        return Object.keys(stagedFilesByDataset[activeRun?.id] || []).filter(sampleName => {
            const stagedFile = stagedFilesByDataset[activeRun.id][sampleName];
            return stagedFile && stagedFile.status === null && stagedFile.canUpload !== false;
        }).length;
    }
    const handleClose = () => {
        if (getStagedFilesNotUploadedCt() > 0) {
            setConfirmClose(true);
        } else {
            handleConfirmClose();
        }
    }
    const handleConfirmClose = () => {
        setConfirmClose(false);
        setOpen(false);
        // remove all staged files that have not been uploaded or that have been succeeded/failed
        let newStagedFiles = Object.values(stagedFilesByDataset[activeRun.id]).reduce((acc, file) => {
            if (file.status === null || ['failed', 'success'].includes(file.status)) {
                return acc;
            }
            acc[file.sampleName] = file;
            return acc;
        }, {});
        setStagedFilesbyDataset(prev => {
            const newStagedFilesByDataset = {...prev};
            newStagedFilesByDataset[activeRun.id] = newStagedFiles;
            return newStagedFilesByDataset;
        });
        // Update the URL to go back to just the project level 
        const url = new URL(activeProject.attributes.keynome_web_url);
        navigate(url.pathname, { state: location.state }); 
    }

    const handleUpdateDatasetSave = async (newDatatsetName) => {
        try {
            await updateDataset({
                datasetId: activeRun.id,
                newDatasetName: slugify(newDatatsetName),
            });
            // Invalidate queries related to the active run to refresh data
            await queryClient.invalidateQueries({
                predicate: ({queryKey}) => queryKey[0].startsWith(`v1/sequencing_runs/${activeRun.id}`) || queryKey[0].startsWith(`v1/projects/${activeProject.id}/sequencing_runs`),
                refetchType: 'all',
            })
            setIsEditing(false);
        } catch (error) {
            setUserErrorMessage('Failed to update dataset name. Please wait a few minutes and try again. Contact support@dayzerodiagnostics.com if you require assistance.');
        }
      };

    return (
        <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xl"
        data-cy="add-samples-dialog"
        >
            <DialogTitle>
                <Grid container>
                    <Grid item xs={8}>
                    {isEditing ? (
                        <UpdateTextField
                            initialValue={activeRun?.attributes.name}
                            valueLabel="Name"
                            helperTextFunc={(value) => `Required. ${value === '' ? '' : `Identifier: ${slugify(value)}`}`}
                            handleSaveClick={handleUpdateDatasetSave}
                            handleCancelClick={() => setIsEditing(false)}
                        />
                    ) : (
                        <Box>
                            {activeRun?.attributes.name}
                            <AddSamplesDialogMenu activeRun={activeRun} isIllumina={isIllumina} setArchiveDialogOpen={setArchiveDialogOpen} setIsEditing={setIsEditing} setMoveDatasetDialogOpen={setMoveDatasetDialogOpen} setUpdateRunDialogOpen={setUpdateRunDialogOpen} />
                        </Box>
                        )}
                        </Grid>
                    <MoveDatasetDialog
                        activeProject={activeProject}
                        activeRun={activeRun}
                        moveDatasetDialogOpen={moveDatasetDialogOpen}
                        setMoveDatasetDialogOpen={setMoveDatasetDialogOpen}
                        setAddSampleDialogOpen={setOpen}
                        setReloadRuns={setReloadRuns}
                        handleClose={handleClose}

                    />
                    <ArchiveDatasetDialog
                        activeProject={activeProject}
                        activeRun={activeRun}
                        archiveDialogOpen={archiveDialogOpen}
                        setArchiveDialogOpen={setArchiveDialogOpen}
                        setReloadRuns={setReloadRuns}
                        handleClose={handleClose}
                    />
                    <Grid item xs={4} sx={{textAlign: "right"}}>
        { isIllumina ? (
                        <>
                        <img src={illuminaLogo} alt="platform-logo" height="30px" style={{marginBottom: '-8px', marginLeft: '4px', marginRight: '10px'}} />
                        Illumina Paired End
            </>

        ) : (
            <>
                        <img src={ontLogo} alt="platform-logo" height="30px" style={{marginBottom: '-8px', marginLeft: '4px', marginRight: '10px'}} />
                        ONT Single End
            </>
        )}
                    </Grid>
                </Grid>
      <Grid item xs={12}>
        <Box>
            <Grid container>
              <Grid item xs={6}>
                    <div style={{color: '#797979', marginBottom: '20px', marginTop: '20px', fontSize: 15}}>
                        <b>UPLOAD SEQUENCING FILES</b>
                    </div>
              </Grid>
              <Grid item xs={6} sx={{textAlign: "right"}}>
            <div style={{color: '#797979', marginBottom: '20px', marginTop: '20px', fontSize: 15}}>
                Keynome Manager Upload ID: <pre style={{display: "inline", backgroundColor: "#eee", padding: "5px 10px", borderRadius: "5px"}} >{activeRun?.attributes.uuid}</pre>
            </div>
            </Grid>
            </Grid>
            </Box>
            </Grid>
                <FileDropZone 
                  isIllumina={isIllumina} 
                  addStagedFile={addStagedFile} 
                  activeRun={activeRun}
                />
    {Object.keys(stagedFilesByDataset[activeRun?.id] || []).length ? (
    <>
      <Box sx={{ backgroundColor: '#eee', padding: '1px 10px', color: 'black', marginTop: '20px', fontSize: 15 }}>
        <Grid container sx={{ pl: 1, pr: 1, textAlign: 'center' }}>
          <Grid item xs={12} sm={2} sx={{ textAlign: 'left' }}>
            <p style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <b>Sample Name</b>
                <Tooltip 
                    title="Filenames are used to identify uploaded samples - please ensure no PHI/PII is included in provided filenames."
                    arrow
                >
                    <InfoIcon 
                        sx={{ 
                            color: 'rgba(0, 0, 0, 0.4)',
                            fontSize: 20,
                            cursor: 'pointer'
                        }} 
                    />
                </Tooltip>
            </p>
          </Grid>
          <Grid item xs={1.5} sx={{ textAlign: 'center' }}>
            <p>Sample Type</p>
          </Grid>
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
            <p>{isIllumina ? 'R1 ' : ''}File Name</p>
          </Grid>
        {
            isIllumina ? 
            (
              <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
                <p>R2 File Name</p>
              </Grid>
            ) : null

        }
          <Grid item xs={12} sm={1.5} sx={{ textAlign: 'center' }}>
            <p>{isIllumina ? 'Total ' : ''}File Size</p>
          </Grid>
          {isIllumina ? null : (
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
            <p>File Last Modified</p>
          </Grid>
          )}
          <Grid item xs={12} sm={3} sx={{ textAlign: 'center' }}>
            &nbsp;
          </Grid>
        </Grid>
      </Box>
      <List sx={{
          width: '100%', bgcolor: 'background.paper', paddingTop: '0', paddingBottom: '0', maxHeight: "200px", overflowY: 'auto'
      }}
      >
        {Object.values(stagedFilesByDataset[activeRun?.id]).map(stagedFile => {
            return <AddSamplesFileListItem key={`${activeRun?.id}-${stagedFile.sampleName}`} activeRun={activeRun} isIllumina={isIllumina} stagedFile={stagedFile} removeStagedFile={removeStagedFile} setStagedFileAttribute={setStagedFileAttribute} uploadFile={uploadFile} />
        })}
      </List>
                </>
    ) : null}
        <Divider sx={{margin: '40px 40px 0 40px'}} />
            </DialogTitle>
            <DialogContent>
      <Grid container>
        <Grid item xs={6}>
          <p style={{color: '#797979', marginBottom: '20px'}}><b>SAMPLES IN DATASET</b></p>
        </Grid>
        <Grid item xs={6} sx={{ textAlign: "right" }}>
          <TriggerClonalityAnalysisButtonOrStatusIcon activeRun={activeRun} samples={samples}/>
        </Grid>
      </Grid>
          <SamplesList samples={visibleSamples} activeProject={activeProject} selectedRun={activeRun} isIllumina={isIllumina}/>
            </DialogContent>
            <DialogActions sx={{marginBottom: "15px"}}>
                {
                    backgroundUploadingFilesCt === 0 ?
                        null :
                        (
                            <p style={{color: "gray", marginRight: "20px", textAlign: "right"}}><small>Files may take a few minutes to upload<br />Feel free to close and upload{backgroundUploadingFilesCt === 1 ? '' : 's'} will continue in the background</small></p>
                        )
                }
                <Button variant="contained" aria-label="close" color="primary" sx={{marginRight: "20px"}} onClick={() => {
                    handleClose()
                }} >Close</Button>
            </DialogActions>
            <Dialog data-cy="unsaved-changes-dialog" open={confirmClose} onClose={() => setConfirmClose(false)}>
                <DialogTitle>Unsaved Changes</DialogTitle>
                <DialogContent>
                    You have {getStagedFilesNotUploadedCt()} file{getStagedFilesNotUploadedCt() === 1 ? '' : 's'} staged for upload that have not been uploaded. Are you sure you want to exit without uploading?
                </DialogContent>
                <DialogActions>
                    <Button aria-label="cancel-close" onClick={() => setConfirmClose(false)} variant="contained">Cancel</Button>
                    <Button aria-label="confirm-close" onClick={handleConfirmClose} >Exit Without Uploading</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
        
    );
}

function AddSamplesDialogMenu(props) {
    const {
        activeRun,
        isIllumina,
        setArchiveDialogOpen,
        setMoveDatasetDialogOpen,
        setIsEditing,
        setUpdateRunDialogOpen
    } = props;

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuItemIsExporting, setMenuItemIsExporting] = useState(true);

    const { data: samples, isLoading: samplesLoading } = useSamples(activeRun?.id);
    const { data: activeAnalyses, isLoading: activeAnalysesLoading } = useActiveAnalysis(
        samples?.map(sample => sample.id)
    )

    useEffect(() => {
        setMenuItemIsExporting(true);
        if (samplesLoading || activeAnalysesLoading || !samples) {return;}

        const completeAnalyses = activeAnalyses.filter(analysis => analysis?.attributes.status === 'succeeded');
        if (completeAnalyses.length) setMenuItemIsExporting(false);
    }, [activeAnalysesLoading, samples, samplesLoading, activeAnalyses, setMenuItemIsExporting]);

    const handleMenuClick = (event) => {
        setMenuAnchorEl(event.currentTarget);
      };
      
    const handleMenuClose = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        setMenuAnchorEl(null);
    };
    
    const handleArchiveClick = () => {
        setArchiveDialogOpen(true);
        handleMenuClose();
    };
    const handleMoveDatasetClick = () => {
        setMoveDatasetDialogOpen(true);
        handleMenuClose();
    };



    return <>
    <IconButton
        size="small"
        onClick={handleMenuClick}
        sx={{ ml: 1 }}
        aria-label="sample-dialog-menu-button"
    >
    <MoreVertIcon />
    </IconButton>
    <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClick}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
    >
    <ExportIdentifiedSpeciesMenuItem
        isIllumina={isIllumina}
        isDatasetLevel={true}
        run={activeRun}
        samples={samples}
        handleMenuClose={handleMenuClose}
        oneMenuItemClicked={menuItemIsExporting}
        setOneMenuItemClicked={setMenuItemIsExporting}
    />
    <ExportKgASTPredictionsMenuItem
        isIllumina={isIllumina}
        isDatasetLevel={true}
        run={activeRun}
        samples={samples}
        handleMenuClose={handleMenuClose}
        oneMenuItemClicked={menuItemIsExporting}
        setOneMenuItemClicked={setMenuItemIsExporting}
    />
    <MenuItem
        aria-label="edit-dataset-name"
        onClick={() => {setIsEditing(true)}}
    >
        Edit Dataset Name
    </MenuItem>
    <MenuItem onClick={(event) => {
        handleMenuClose(event);
        setUpdateRunDialogOpen(true);
    }}>
        Edit Dataset Metadata
    </MenuItem>
    <MenuItem cy="move-dataset" onClick={(event) => {
        handleMoveDatasetClick();
        handleMenuClose(event);
    }}>
        Move Dataset to Project
    </MenuItem>
    <MenuItem aria-label="archive-dataset" onClick={(event) => {
        handleArchiveClick();
        handleMenuClose(event);
    }}>
        Archive Dataset
    </MenuItem>
    </Menu>
    </>
}

export default AddSamplesDialog;
